import { useCallback, useMemo } from 'react';
import { Toggle } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import type { MultiSelectOption } from '@workpay/ds-shared/components/ui/MultiSelect';
import type { SelectOption } from '@workpay/ds-shared/components/ui/Select';
import { useTranslation } from '@hooks/useTranslation';
import type { FormTemplateFormEntry } from '@/types/formTemplate';
import type { CountryDto, ProductTypeDto } from '@/types/tenant';
import styles from './FormSettingsPanel.module.scss';

// ============================================
// TYPES
// ============================================

interface FormSettingsPanelProps {
  entry: FormTemplateFormEntry;
  onChange: (updated: FormTemplateFormEntry) => void;
  countries: CountryDto[];
  productTypes: ProductTypeDto[];
  countriesLoading: boolean;
  productTypesLoading: boolean;
  countryError?: string;
  productTypeError?: string;
}

// ============================================
// COMPONENT
// ============================================

function FormSettingsPanel({
  entry,
  onChange,
  countries,
  productTypes,
  countriesLoading,
  productTypesLoading,
  countryError,
  productTypeError,
}: FormSettingsPanelProps) {
  const { t } = useTranslation();

  const ACCESS_LEVEL_OPTIONS: SelectOption[] = useMemo(() => [
    { label: t('K_ACCESS_READ_WRITE', 'Read & Write'), value: 'READ_WRITE' },
    { label: t('K_ACCESS_READ_ONLY', 'Read Only'), value: 'READ' },
    { label: t('K_ACCESS_NO_ACCESS', 'No Access'), value: 'NO_ACCESS' },
  ], [t]);
  const countryOptions: MultiSelectOption[] = countries.map((c) => ({
    label: c.name,
    value: c.id,
  }));

  const productTypeOptions: MultiSelectOption[] = productTypes.map((pt) => ({
    label: pt.productType,
    value: pt.id,
    description: pt.productDesc,
  }));

  const handleAllCountriesChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...entry,
        filters: {
          ...entry.filters,
          country: { matchAll: checked, values: checked ? [] : entry.filters.country.values },
        },
      });
    },
    [entry, onChange],
  );

  const handleCountryIdsChange = useCallback(
    (ids: string[]) => {
      onChange({
        ...entry,
        filters: {
          ...entry.filters,
          country: { ...entry.filters.country, values: ids },
        },
      });
    },
    [entry, onChange],
  );

  const handleAllProductTypesChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...entry,
        filters: {
          ...entry.filters,
          productType: { matchAll: checked, values: checked ? [] : entry.filters.productType.values },
        },
      });
    },
    [entry, onChange],
  );

  const handleProductTypeIdsChange = useCallback(
    (ids: string[]) => {
      onChange({
        ...entry,
        filters: {
          ...entry.filters,
          productType: { ...entry.filters.productType, values: ids },
        },
      });
    },
    [entry, onChange],
  );

  const handleAccessLevelChange = useCallback(
    (value: string) => {
      onChange({
        ...entry,
        accessLevel: value as FormTemplateFormEntry['accessLevel'],
      });
    },
    [entry, onChange],
  );

  const handleIsDefaultChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...entry,
        attributes: { ...entry.attributes, isDefault: e.target.checked },
      });
    },
    [entry, onChange],
  );

  const handleIsMandatoryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...entry,
        attributes: { ...entry.attributes, isMandatory: e.target.checked },
      });
    },
    [entry, onChange],
  );

  return (
    <div className={styles.grid}>
      {/* Countries */}
      <div className={styles.fieldGroup}>
        <MultiSelect
          label={t('K_LABEL_COUNTRIES', 'Countries')}
          options={countryOptions}
          value={entry.filters.country.values}
          onChange={handleCountryIdsChange}
          disabled={entry.filters.country.matchAll}
          loading={countriesLoading}
          searchable
          placeholder={entry.filters.country.matchAll ? t('K_PLACEHOLDER_ALL_COUNTRIES', 'All countries selected') : t('K_PLACEHOLDER_SELECT_COUNTRIES', 'Select countries')}
          fullWidth
          error={!entry.filters.country.matchAll ? countryError : undefined}
        />
        <Toggle
          label={t('K_LABEL_SELECT_ALL_COUNTRIES', 'Select all countries')}
          isChecked={entry.filters.country.matchAll}
          onChange={handleAllCountriesChange}
        />
      </div>

      {/* Product Types */}
      <div className={styles.fieldGroup}>
        <MultiSelect
          label={t('K_LABEL_PRODUCT_TYPES', 'Product Types')}
          options={productTypeOptions}
          value={entry.filters.productType.values}
          onChange={handleProductTypeIdsChange}
          disabled={entry.filters.productType.matchAll}
          loading={productTypesLoading}
          searchable
          placeholder={
            entry.filters.productType.matchAll ? t('K_PLACEHOLDER_ALL_PRODUCT_TYPES', 'All product types selected') : t('K_PLACEHOLDER_SELECT_PRODUCT_TYPES', 'Select product types')
          }
          fullWidth
          error={!entry.filters.productType.matchAll ? productTypeError : undefined}
        />
        <Toggle
          label={t('K_LABEL_SELECT_ALL_PRODUCT_TYPES', 'Select all product types')}
          isChecked={entry.filters.productType.matchAll}
          onChange={handleAllProductTypesChange}
        />
      </div>

      {/* Access Level */}
      <div>
        <Select
          label={t('K_LABEL_CLIENT_ACCESS', 'Client access')}
          options={ACCESS_LEVEL_OPTIONS}
          value={entry.accessLevel}
          onChange={handleAccessLevelChange}
          fullWidth
        />
      </div>

      {/* Default Form + Mandatory Form — Checkboxes */}
      <div className={styles.defaultFormCell}>
        <label className={styles.defaultFormLabel}>
          <input
            type="checkbox"
            checked={entry.attributes.isDefault}
            onChange={handleIsDefaultChange}
            className={styles.defaultFormCheckbox}
          />
          <span>{t('K_CLIENT_FORM', 'Client form')}</span>
        </label>
        <label className={styles.defaultFormLabel}>
          <input
            type="checkbox"
            checked={entry.attributes.isMandatory ?? false}
            onChange={handleIsMandatoryChange}
            className={styles.defaultFormCheckbox}
          />
          <span>{t('K_MANDATE_FORM', 'Mandate form while creating client')}</span>
        </label>
      </div>
    </div>
  );
}

export default FormSettingsPanel;
